export interface PlanFeatures {
    platformFeatures: PlatformFeatures;
    dataProtectionFeatures: DataProtectionFeatures;
    informationSecurityFeatures: InformationSecurityFeatures;
    vendorManagementFeatures: VendorManagementFeatures;
    addonsFeatures: AddonFeatures;
    contractManagementFeatures: ContractManagementFeatures;
    frameworkFeatures: FrameworkFeatures;
}

export interface ApplicationFeatures {
    planFeatures: PlanFeatures;
    featureFlags: Record<string, boolean>;
}

export class FeatureFlag {
    public static readonly FrameworkCertification: string = "FrameworkCertification";
    public static readonly PenneoIntegration: string = "PenneoIntegration";
    public static readonly Activities: string = "Activities";
    public static readonly HideAnnualWheel: string = "HideAnnualWheel";
    public static readonly AiDocumentDescription: string = "AiDocumentDescription";
    public static readonly IncidentLogTemplates: string = "IncidentLogTemplates";
    public static readonly NewProcessingActivity: string = "NewProcessingActivity";
    public static readonly ProcessTemplates: string = "ProcessTemplates";
    public static readonly CalculatedFieldFormulas: string = "CalculatedFieldFormulas";
    public static readonly EntityTemplateDefaultValues: string = "EntityTemplateDefaultValues";
    public static readonly ContractDashboard: string = "ContractDashboard";
}

export interface PlatformFeatures {
    canDownloadPolicyTemplates: boolean;
    maxNumberOfGroupCompanies?: number;
    dashboard: boolean;
    maxNumberOfPolicies?: number;
    maxNumberOfProcesses?: number;
    commentModule: boolean;
    addTasksToOutlook: boolean;
    annualWheelOverview: boolean;
    annualWheelReport: boolean;
    annualWheelCopyActivity: boolean;
    hasTaskManagementAccess: boolean;
    taskHistory: boolean;
    assignTask: boolean;
    signOff: boolean;
    annualWheelComplianceArea: boolean;
    riskVersions: boolean;
    tags: boolean;
    relatedActivitiesAndTasks: boolean;
    auditLog: boolean;
    canAccessRelationsBetweenEntities: boolean;
    canManagePageView: boolean;
    canSaveTableConfiguration: boolean;
    canTransferUserResponsibility: boolean;
    canTransferBusinessAreaAccess: boolean;
}

export interface DataProtectionFeatures {
    maxNumberOfProcessingActivities?: number;
    processingActivityTemplates: ProcessingActivityTemplates;
    processingActivityValidationFlow: boolean;
    dataProcessingAgreements: boolean;
    processingActivitiesDocuments: boolean;
    standardGDPRActivities: StandardGDPRActivities;
    maxNumberOfProcessingActivityRiskAssessments?: number;
    maxNumberOfGDPRRiskScenarios?: number;
    gdprRiskAssessmentConsequenceCatalogue: boolean;
    gdprRiskReports: boolean;
    maxNumberOfIncidentLogEntries?: number;
    processingActivityStatus: boolean;
    registrationOfDataController: boolean;
    sharedResponsibility: boolean;
    linkPoliciesAndProcessingActivities: boolean;
    recordDataProcessor: boolean;
    recordsExport: boolean;
    standardListExport: boolean;
    maxNumberOfContactsOnUnit?: number;
}

export interface InformationSecurityFeatures {
    maxNumberOfSystems?: number;
    hasInfoSec: boolean;
    systemTemplates: SystemTemplates;
    systemDocuments: boolean;
    niS2ActivitiesCatalogue: StandardNIS2Activities;
    isoActivitiesCatalogue: boolean;
    maxNumberOfAssetRiskAssessments?: number;
    maxNumberOfNIS2RiskScenarios?: number;
    nis2RiskAssessmentConsequenceCatalogue: boolean;
    niS2RiskReports: boolean;
    systemRiskClassification: boolean;
    standardListExport: boolean;
    storageType: boolean;
    entityType: boolean;
    dataType: boolean;
    certifications: boolean;
    entityTypeFilter: boolean;
    associatedAssets: boolean;
    internalControlProcedures: boolean;
    personalData: boolean;
    automaticRegistrationOfDataProcessorTransfers: boolean;
    ai: boolean;
    legalEntityDocuments: boolean;
}

export interface VendorManagementFeatures {
    hasVendorManagement: boolean;
    maxNumberOfVendorRiskClassificationsPerYear?: number;
    maxNumberOfExternalAuditsPerYear?: number;
    maxNumberOfInternalAuditsPerYear?: number;
    auditGroups: boolean;
    internalAssessmentGroups: boolean;
    vendorDocuments: boolean;
    maxNumberOfContactsOnUnit?: number;
    standardListsExport: boolean;
    certifications: boolean;
    processingActivityAssociateInternalAssessments: boolean;
    systemAssociateInternalAssessments: boolean;
    publicAssessments: boolean;
    systemClassifications: boolean;
    legalEntityContacts: boolean;
}

export interface ContractManagementFeatures {
    hasContractManagement: boolean;
    maxNumberOfContracts?: number;
}

export interface FrameworkFeatures {
    maxNumberOfFrameworks?: number;
    canCreateCustomFrameworks: boolean;
}

export interface AddonFeatures {
    sso: boolean;
    customLists: boolean;
    organizationalManagement: boolean;
    research: boolean;
    declarations: boolean;
    userManagementPremium: boolean;
    cvr: boolean;
    obligations: boolean;
    entityTemplates: boolean;
    searchService: boolean;
    aiFeatures: boolean;
    policyManagement: boolean;
    enterpriseRiskManagement: boolean;
    publicApi: boolean;
}

export enum StandardGDPRActivities {
    NoAccess = 0,
    MostPopular = 1,
    All = 2,
}

export enum StandardNIS2Activities {
    NoAccess = 0,
    MostPopular = 1,
    All = 2,
}

export enum ProcessingActivityTemplates {
    MostPopular,
    All,
}

export enum SystemTemplates {
    MostPopular,
    All,
}

export enum PlanType {
    DotLegalCompliance = 400,
    DataProtection = 401,
    InformationSecurity = 402,
    VendorManagement = 403,
    Research = 404,
    Declarations = 405,
    SingleSignOn = 406, // add-on
    UserManagementPremium = 407, // add-on
    CustomLists = 408, // add-on
    OrganizationalManagement = 409, // add-on
    ContractManagement = 410,
    Cvr = 411, // add-on
    Frameworks = 412,
    Obligations = 413, // add-on
    EntityTemplates = 414, // add-on
    SearchService = 415, // add-on
    AIFeatures = 416,
    PolicyManagement = 417, // add-on
    EnterpriseRiskManagement = 418, // add-on
    PublicApi = 419, // add-on
}

export interface PlanLimit {
    limitCount: number;
    limitReached: boolean;
    limitExceeded: boolean;
    warnLimitReachedOnTrial: boolean;
    currentCount: number;
}
